import { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { MdSearch } from 'react-icons/md';

const mockAuditLogs = [
  {
    id: 1,
    admin: 'John Admin',
    action: 'Updated Plan',
    object: 'Premium Plan',
    oldValue: '₦12,000/month',
    newValue: '₦15,000/month',
    timestamp: '2026-02-25 14:32:15',
  },
  {
    id: 2,
    admin: 'Sarah Manager',
    action: 'Disabled Feature',
    object: 'Custom Branding',
    oldValue: 'Active',
    newValue: 'Inactive',
    timestamp: '2026-02-25 12:18:42',
  },
  {
    id: 3,
    admin: 'John Admin',
    action: 'Created User Override',
    object: 'user@example.com',
    oldValue: 'Pro Plan',
    newValue: 'Premium Plan',
    timestamp: '2026-02-25 10:05:33',
  },
  {
    id: 4,
    admin: 'Emily Tech',
    action: 'Updated Service Fee',
    object: 'Pro Plan - Flight',
    oldValue: '3.0%',
    newValue: '2.5%',
    timestamp: '2026-02-24 16:45:20',
  },
  {
    id: 5,
    admin: 'John Admin',
    action: 'Created Plan',
    object: 'Enterprise Plan',
    oldValue: 'N/A',
    newValue: 'Custom Pricing',
    timestamp: '2026-02-24 14:22:10',
  },
  {
    id: 6,
    admin: 'Sarah Manager',
    action: 'Enabled Feature',
    object: 'Priority Support',
    oldValue: 'Inactive',
    newValue: 'Active',
    timestamp: '2026-02-24 11:30:55',
  },
  {
    id: 7,
    admin: 'Emily Tech',
    action: 'Updated Team Pricing',
    object: 'Pro Seat Price',
    oldValue: '₦4,500',
    newValue: '₦5,000',
    timestamp: '2026-02-23 15:12:08',
  },
  {
    id: 8,
    admin: 'John Admin',
    action: 'Deleted Plan',
    object: 'Starter Plan',
    oldValue: 'Active',
    newValue: 'Deleted',
    timestamp: '2026-02-23 09:45:30',
  },
  {
    id: 9,
    admin: 'Sarah Manager',
    action: 'Updated Feature',
    object: 'API Access',
    oldValue: 'Premium Only',
    newValue: 'Pro + Premium',
    timestamp: '2026-02-22 17:20:18',
  },
  {
    id: 10,
    admin: 'Emily Tech',
    action: 'Updated Grace Period',
    object: 'Pro Plan',
    oldValue: '5 days',
    newValue: '3 days',
    timestamp: '2026-02-22 13:55:42',
  },
];

const getActionColor = (action) => {
  if (action.includes('Created') || action.includes('Enabled')) {
    return { bg: '#DCFCE7', color: '#16A34A' };
  }
  if (action.includes('Updated')) {
    return { bg: '#DBEAFE', color: '#1F4FFF' };
  }
  if (action.includes('Deleted') || action.includes('Disabled')) {
    return { bg: '#FEE2E2', color: '#DC2626' };
  }
  return { bg: '#F1F5F9', color: '#64748B' };
};

export default function AuditLogs() {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAdmin, setFilterAdmin] = useState('all');
  const [filterAction, setFilterAction] = useState('all');

  return (
    <Box>
      {/* Header */}
      <Box className="mb-6">
        <Typography variant="h5" className="font-bold text-slate-900">
          Audit Logs
        </Typography>
        <Typography variant="body2" className="text-slate-600 mt-1">
          Track all administrative actions and system changes
        </Typography>
      </Box>

      {/* Filter Panel */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: '16px',
          border: '1px solid #E2E8F0',
        }}
      >
        <Box className="flex flex-col md:flex-row gap-3">
          <TextField
            placeholder="Search logs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MdSearch className="text-slate-400" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Admin</InputLabel>
            <Select
              value={filterAdmin}
              label="Admin"
              onChange={(e) => setFilterAdmin(e.target.value)}
            >
              <MenuItem value="all">All Admins</MenuItem>
              <MenuItem value="john">John Admin</MenuItem>
              <MenuItem value="sarah">Sarah Manager</MenuItem>
              <MenuItem value="emily">Emily Tech</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Action Type</InputLabel>
            <Select
              value={filterAction}
              label="Action Type"
              onChange={(e) => setFilterAction(e.target.value)}
            >
              <MenuItem value="all">All Actions</MenuItem>
              <MenuItem value="created">Created</MenuItem>
              <MenuItem value="updated">Updated</MenuItem>
              <MenuItem value="deleted">Deleted</MenuItem>
              <MenuItem value="enabled">Enabled</MenuItem>
              <MenuItem value="disabled">Disabled</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Audit Logs Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: '16px',
          border: '1px solid #E2E8F0',
          overflow: 'hidden',
        }}
      >
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: '#475569',
                    bgcolor: '#F8FAFC',
                  }}
                >
                  Admin
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: '#475569',
                    bgcolor: '#F8FAFC',
                  }}
                >
                  Action
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: '#475569',
                    bgcolor: '#F8FAFC',
                  }}
                >
                  Object
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: '#475569',
                    bgcolor: '#F8FAFC',
                  }}
                >
                  Old Value
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: '#475569',
                    bgcolor: '#F8FAFC',
                  }}
                >
                  New Value
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: '#475569',
                    bgcolor: '#F8FAFC',
                  }}
                >
                  Timestamp
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mockAuditLogs.map((log) => {
                const actionColor = getActionColor(log.action);
                return (
                  <TableRow
                    key={log.id}
                    sx={{
                      '&:hover': {
                        bgcolor: '#F8FAFC',
                      },
                    }}
                  >
                    <TableCell>
                      <Typography className="font-semibold text-slate-900">
                        {log.admin}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={log.action}
                        size="small"
                        sx={{
                          bgcolor: actionColor.bg,
                          color: actionColor.color,
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography className="text-slate-700 font-medium">
                        {log.object}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography className="text-slate-600 font-mono text-sm">
                        {log.oldValue}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography className="text-slate-900 font-mono text-sm font-semibold">
                        {log.newValue}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography className="text-slate-600 text-sm">
                        {log.timestamp}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
